// Sales Partner Sign In Page
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { usePartnerAuth } from '../../auth/PartnerAuthProvider';

export default function PartnerSignInPage() {
  const { login } = usePartnerAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    try {
      setBusy(true);
      await login(email, password);
      navigate('/partner/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="auth-modern d-flex flex-wrap min-vh-100">
      {/* Left Side - Branding */}
      <div
        className="auth-left-modern d-lg-flex d-none flex-column justify-content-center align-items-center p-5"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div className="position-relative z-1 text-center w-100" style={{ maxWidth: '500px' }}>
          <div className="mb-5">
            <Icon
              icon="mdi:handshake"
              style={{
                fontSize: '120px',
                color: 'white',
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
              }}
            />
          </div>

          <h1 className="text-white fw-bold mb-4" style={{ fontSize: '3rem' }}>
            RideWatch Partners
          </h1>
          <h2 className="text-white fw-bold mb-3" style={{ fontSize: '1.8rem' }}>
            Welcome Back
          </h2>
          <p className="text-white opacity-90 fs-5 mb-5">
            Access your partner dashboard to track referrals and earnings
          </p>

          {/* Stats Preview */}
          <div className="d-flex justify-content-center gap-4 mt-5">
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:school" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">Schools</small>
            </div>
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:account-group" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">Subscribers</small>
            </div>
            <div className="text-center">
              <div
                className="bg-white bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mb-2"
                style={{ width: '60px', height: '60px' }}
              >
                <Icon icon="mdi:cash-multiple" style={{ fontSize: '30px', color: 'white' }} />
              </div>
              <small className="text-white opacity-75">Earnings</small>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div
          className="position-absolute"
          style={{
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            top: '-100px',
            right: '-100px',
            zIndex: 0,
          }}
        />
        <div
          className="position-absolute"
          style={{
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            bottom: '50px',
            left: '-50px',
            zIndex: 0,
          }}
        />
      </div>

      {/* Right Side - Form */}
      <div
        className="auth-right-modern d-flex align-items-center justify-content-center p-4"
        style={{
          flex: 1,
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
        }}
      >
        <div className="w-100" style={{ maxWidth: '480px' }}>
          <div className="bg-white rounded-4 shadow-lg p-5">
            {/* Mobile Logo */}
            <div className="d-lg-none text-center mb-4">
              <Icon icon="mdi:handshake" style={{ fontSize: '60px', color: '#10b981' }} />
              <h2 className="fw-bold mt-2" style={{ color: '#10b981' }}>
                RideWatch Partners
              </h2>
            </div>

            <div className="text-center mb-4">
              <h3 className="fw-bold mb-2" style={{ color: '#2d3748' }}>
                Partner Sign In
              </h3>
              <p className="text-muted">Access your partner dashboard</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Email Address
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:email" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="email"
                    className="form-control ps-5 py-3"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-4">
                <label className="form-label fw-semibold" style={{ color: '#4a5568' }}>
                  Password
                </label>
                <div className="position-relative">
                  <span className="position-absolute top-50 translate-middle-y ms-3" style={{ zIndex: 10 }}>
                    <Icon icon="mdi:lock" style={{ fontSize: '20px', color: '#9ca3af' }} />
                  </span>
                  <input
                    type="password"
                    className="form-control ps-5 py-3"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '15px',
                    }}
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger rounded-3 py-2" role="alert" style={{ fontSize: '14px' }}>
                  <Icon icon="mdi:alert-circle" className="me-2" />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="btn w-100 py-3 fw-semibold text-white mb-3"
                disabled={busy}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                }}
              >
                {busy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:login" className="me-2" style={{ fontSize: '18px' }} />
                    Sign In
                  </>
                )}
              </button>

              {/* Register Link */}
              <div className="text-center mt-4">
                <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                  Don't have a partner account?{' '}
                  <Link to="/partner/register" className="text-decoration-none fw-bold" style={{ color: '#10b981' }}>
                    Register Now
                  </Link>
                </p>
              </div>

              {/* Regular User Link */}
              <div className="text-center mt-3">
                <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                  Not a partner?{' '}
                  <Link to="/sign-in" className="text-decoration-none" style={{ color: '#667eea' }}>
                    Sign in as Bus Company
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
